<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import { useFavoriteWhatsAppTemplates } from 'dashboard/composables/useFavoriteWhatsAppTemplates';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
  uploadedAudience: [],
};

const sampleAudienceCsv = 'telefono,nombre\n595981000000,Cliente Ejemplo\n';

const state = reactive({ ...initialState });
const templateParserRef = ref(null);
const fileInputRef = ref(null);
const selectedFileName = ref('');
const uploadError = ref('');
const uploadSummary = ref('');
const showAudienceError = ref(false);
const {
  isFavoriteTemplate,
  toggleFavoriteTemplate,
  favoriteTemplatesFrom,
  sortTemplatesByFavorite,
} = useFavoriteWhatsAppTemplates(computed(() => state.inboxId));

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);
const hasAudienceSelection = computed(
  () =>
    state.selectedAudience.length > 0 || state.uploadedAudience.length > 0
);
const audienceError = computed(
  () => showAudienceError.value && !hasAudienceSelection.value
);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = sortTemplatesByFavorite(
    formState.getFilteredWhatsAppTemplates.value(state.inboxId)
  );
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template,
    };
  });
});

const favoriteTemplates = computed(() =>
  favoriteTemplatesFrom(
    formState.getFilteredWhatsAppTemplates.value(state.inboxId) || []
  ).slice(0, 6)
);

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: audienceError.value
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ERROR')
    : '',
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.isFormInvalid === false;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const normalizeHeader = value => value?.toString().trim().toLowerCase() || '';

const normalizePhoneNumber = value => {
  if (!value) return '';

  const sanitizedNumber = value.toString().trim().replace(/[^\d+]/g, '');
  if (!sanitizedNumber) return '';

  if (sanitizedNumber.startsWith('+')) {
    return `+${sanitizedNumber.replace(/\+/g, '')}`;
  }

  return sanitizedNumber;
};

const parseCsvRows = content => {
  const rows = [];
  let currentRow = [];
  let currentValue = '';
  let inQuotes = false;

  const normalizedContent = content.replace(/^\uFEFF/, '');

  for (let index = 0; index < normalizedContent.length; index += 1) {
    const char = normalizedContent[index];
    const nextChar = normalizedContent[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentValue += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      currentRow.push(currentValue);
      currentValue = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }

      currentRow.push(currentValue);
      rows.push(currentRow);
      currentRow = [];
      currentValue = '';
      continue;
    }

    currentValue += char;
  }

  if (currentValue || currentRow.length) {
    currentRow.push(currentValue);
    rows.push(currentRow);
  }

  return rows.filter(row => row.some(column => column?.trim()));
};

const resetFileSelection = () => {
  selectedFileName.value = '';
  uploadError.value = '';
  uploadSummary.value = '';
  state.uploadedAudience = [];

  if (fileInputRef.value) {
    fileInputRef.value.value = null;
  }
};

const downloadAudienceTemplate = () => {
  downloadCsvFile(
    'chatmigo-campana-whatsapp-ejemplo.csv',
    sampleAudienceCsv
  );
};

const openFileDialog = () => fileInputRef.value?.click();

const removeUploadedAudience = () => {
  resetFileSelection();
};

const parseAudienceFile = async file => {
  const lowerCaseName = file.name.toLowerCase();
  if (!lowerCaseName.endsWith('.csv')) {
    throw new Error(
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.ERROR_CSV_ONLY')
    );
  }

  const content = await file.text();
  const rows = parseCsvRows(content);

  if (rows.length <= 1) {
    throw new Error(t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.ERROR_EMPTY'));
  }

  const headers = rows[0].map(normalizeHeader);
  const phoneHeaderIndex = headers.findIndex(header =>
    ['phone_number', 'phone', 'telefono', 'teléfono'].includes(header)
  );
  const nameHeaderIndex = headers.findIndex(header =>
    ['name', 'nombre'].includes(header)
  );

  if (phoneHeaderIndex === -1) {
    throw new Error(
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.ERROR_HEADERS')
    );
  }

  const audienceByPhone = new Map();
  let ignoredRows = 0;

  rows.slice(1).forEach(row => {
    const phoneNumber = normalizePhoneNumber(row[phoneHeaderIndex]);
    const name = (row[nameHeaderIndex] || '').toString().trim();

    if (!phoneNumber) {
      ignoredRows += 1;
      return;
    }

    audienceByPhone.set(phoneNumber, {
      type: 'UploadedContact',
      id: phoneNumber,
      phone_number: phoneNumber,
      name,
    });
  });

  if (!audienceByPhone.size) {
    throw new Error(t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.ERROR_EMPTY'));
  }

  return {
    recipients: [...audienceByPhone.values()],
    ignoredRows,
  };
};

const handleFileChange = async event => {
  const file = event.target.files?.[0];

  if (!file) {
    resetFileSelection();
    return;
  }

  uploadError.value = '';
  uploadSummary.value = '';

  try {
    const { recipients, ignoredRows } = await parseAudienceFile(file);

    state.uploadedAudience = recipients;
    selectedFileName.value = file.name;
    uploadSummary.value = ignoredRows
      ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.SUMMARY_WITH_IGNORED', {
          count: recipients.length,
          ignored: ignoredRows,
        })
      : t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.SUMMARY', {
          count: recipients.length,
        });
  } catch (error) {
    resetFileSelection();
    uploadError.value =
      error?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.ERROR_EMPTY');
  }
};

const resetState = () => {
  Object.assign(state, initialState);
  resetFileSelection();
  showAudienceError.value = false;
  v$.value.$reset();
};

const selectFavoriteTemplate = template => {
  state.templateId = template.id;
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  const templateContent = parserData?.renderedTemplate || '';

  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: [
      ...state.selectedAudience.map(id => ({
        id,
        type: 'Label',
      })),
      ...state.uploadedAudience,
    ],
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  showAudienceError.value = !hasAudienceSelection.value;
  if (
    !isFormValid ||
    !hasAudienceSelection.value ||
    !hasRequiredTemplateParams.value
  ) {
    return;
  }

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);

watch(
  () => [state.selectedAudience.length, state.uploadedAudience.length],
  () => {
    if (hasAudienceSelection.value) {
      showAudienceError.value = false;
    }
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <div
        v-if="favoriteTemplates.length"
        class="mb-2 flex flex-wrap gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-2"
      >
        <Button
          v-for="template in favoriteTemplates"
          :key="`favorite-${template.id}`"
          type="button"
          color="slate"
          variant="faded"
          size="sm"
          icon="i-lucide-star"
          :label="template.name"
          class="!w-fit"
          @click="selectFavoriteTemplate(template)"
        />
      </div>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
      <div v-if="selectedTemplate" class="mt-1 flex justify-end">
        <Button
          type="button"
          color="slate"
          variant="ghost"
          size="sm"
          icon="i-lucide-star"
          :label="
            isFavoriteTemplate(selectedTemplate)
              ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.FAVORITES.REMOVE')
              : t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.FAVORITES.ADD')
          "
          class="!w-fit"
          @click="toggleFavoriteTemplate(selectedTemplate)"
        />
      </div>
    </div>

    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <p class="mt-0 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.HELP') }}
      </p>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="p-3 border rounded-xl border-n-weak bg-n-alpha-2">
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.TITLE') }}
          </p>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.DESCRIPTION') }}
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <Button
            type="button"
            color="slate"
            variant="ghost"
            size="sm"
            :label="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.DOWNLOAD_TEMPLATE')
            "
            icon="i-lucide-download"
            class="!w-fit"
            @click="downloadAudienceTemplate"
          />
          <Button
            v-if="!selectedFileName"
            type="button"
            color="slate"
            variant="ghost"
            size="sm"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.CHOOSE_FILE')"
            icon="i-lucide-upload"
            class="!w-fit"
            @click="openFileDialog"
          />
          <template v-else>
            <Button
              type="button"
              color="slate"
              variant="ghost"
              size="sm"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.CHANGE')"
              class="!w-fit"
              @click="openFileDialog"
            />
            <Button
              type="button"
              color="slate"
              variant="ghost"
              size="sm"
              icon="i-lucide-trash"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FILE_UPLOAD.REMOVE')"
              class="!w-fit"
              @click="removeUploadedAudience"
            />
          </template>
        </div>

        <div v-if="selectedFileName" class="flex flex-col gap-1">
          <p class="mb-0 text-sm text-n-slate-12">
            {{ selectedFileName }}
          </p>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ uploadSummary }}
          </p>
        </div>

        <p v-if="uploadError" class="mb-0 text-xs text-n-ruby-11">
          {{ uploadError }}
        </p>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>

    <input
      ref="fileInputRef"
      type="file"
      accept=".csv,text/csv"
      class="hidden"
      @change="handleFileChange"
    />
  </form>
</template>
